import React, { useEffect, useRef, useState } from "react";

import {
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Skeleton,
  Text,
} from "@musa.tech/design-system";
import { useCollectEvidence } from "@/hooks/useCollectEvidence";
import {
  getEvidenceMediaKind,
  type EvidenceSide,
} from "@/utils/evidenceMediaKind";
import { Play, Pause } from "lucide-react";
import { EvidenceDownloadButton } from "./EvidenceDownloadButton";

interface ModalCollectEvidenceProps {
  collectId: string;
  onOpenChange: (open: boolean) => void;
}

/**
 * Antes/depois da coleta — foto ou vídeo, o que o transportador enviou.
 *
 * Foto renderiza direto (sem estado de "carregando" próprio: já é a evidência,
 * não um player). Vídeo mantém o comportamento existente (skeleton até o
 * primeiro frame decodificar, botão de play/pause por cima).
 */
const EvidencePanel: React.FC<{
  collectId: string;
  side: EvidenceSide;
  label: string;
  src: string | null | undefined;
  isLoading: boolean;
}> = ({ collectId, side, label, src, isLoading }) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isVideoLoading, setIsVideoLoading] = useState(true);
  const isImage = !!src && getEvidenceMediaKind(src) === "image";

  useEffect(() => {
    setIsVideoLoading(!!src);
    setIsPlaying(false);
  }, [src]);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    video.muted = true;
    video.defaultMuted = true;
    video.volume = 0;

    const onPlay = () => setIsPlaying(true);
    const onPause = () => setIsPlaying(false);
    const onEnded = () => setIsPlaying(false);

    video.addEventListener("play", onPlay);
    video.addEventListener("pause", onPause);
    video.addEventListener("ended", onEnded);

    return () => {
      video.removeEventListener("play", onPlay);
      video.removeEventListener("pause", onPause);
      video.removeEventListener("ended", onEnded);
    };
  }, []);

  const togglePlay = () => {
    const video = videoRef.current;
    if (!video || isVideoLoading) return;
    if (video.paused) {
      video.play();
    } else {
      video.pause();
    }
  };

  return (
    <div className="flex flex-col gap-2 flex-1">
      <div className="flex items-center justify-between gap-2">
        <Text
          as="span"
          variant="label"
          size="small"
          className="block text-muted-foreground"
        >
          {label}
        </Text>
        {!isLoading && src && (
          <EvidenceDownloadButton
            url={src}
            collectId={collectId}
            side={side}
            label={label}
          />
        )}
      </div>
      {isLoading ? (
        <Skeleton className="w-full h-52 rounded-md" />
      ) : src && isImage ? (
        <img
          src={src}
          alt={`Evidência ${label.toLowerCase()}`}
          className="w-full h-52 rounded-md bg-neutral-900 object-contain"
        />
      ) : src ? (
        <div
          className={`relative ${isVideoLoading ? "cursor-not-allowed" : "cursor-pointer"}`}
          onClick={togglePlay}
        >
          <video
            ref={videoRef}
            src={src}
            width="100%"
            height="180"
            muted
            onCanPlayThrough={() => setIsVideoLoading(false)}
            onWaiting={() => setIsVideoLoading(true)}
            className="evidence-video bg-neutral-900"
            style={{
              display: "block",
              borderRadius: "6px",
            }}
          />
          <div className="absolute inset-0 z-20 flex items-center justify-center">
            {isVideoLoading ? (
              <div className="bg-black/60 rounded-full p-2">
                <Skeleton className="w-5 h-5 rounded-full" />
              </div>
            ) : (
              <Button
                type="button"
                variant="ghost"
                className="bg-black/60 hover:bg-black/80 text-white rounded-full p-2 transition-colors"
              >
                {isPlaying ? <Pause size={20} /> : <Play size={20} />}
              </Button>
            )}
          </div>
        </div>
      ) : (
        <Text
          as="div"
          variant="body"
          size="small"
          className="w-full h-52 flex items-center justify-center rounded-md bg-muted text-muted-foreground"
        >
          Sem evidência
        </Text>
      )}
    </div>
  );
};

const ModalCollectEvidence: React.FC<ModalCollectEvidenceProps> = ({
  collectId,
  onOpenChange,
}) => {
  const { data, isLoading } = useCollectEvidence(collectId || null);

  return (
    <Dialog open={!!collectId} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-3xl">
        <DialogHeader>
          <DialogTitle>Evidências de coleta</DialogTitle>
        </DialogHeader>

        <div className="flex flex-col sm:flex-row gap-4 py-2">
          <EvidencePanel
            collectId={collectId}
            side="before"
            label="Antes da coleta"
            src={data?.evidence_before}
            isLoading={isLoading}
          />
          <EvidencePanel
            collectId={collectId}
            side="after"
            label="Depois da coleta"
            src={data?.evidence_after}
            isLoading={isLoading}
          />
        </div>

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
          >
            Fechar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ModalCollectEvidence;
